import { TOGETHER_ORG_ID, TOGETHER_ORIGIN } from '@/constants/orgs';
import { ROUTES } from '@/constants/routes';
import {
  configNameFor,
  keyEnvVar,
  keyFilePath,
} from '@/features/mcp-access/generate';
import type { McpAccessProfile } from '@/features/mcp-access/types';
import type { McpServer } from '@/services/mcp-servers';

import type { AiAppsServer } from './types';

// A gateway server as an access profile: org-owned ctr_ keys, sent as a
// Bearer token. Matches the develop line's profileFromGatewayServer.
const profileFromGatewayServer = (server: McpServer): McpAccessProfile => ({
  id: server.slug,
  name: server.name,
  description:
    server.description ||
    'MCP endpoint publishing this organization’s workflows, agents and data.',
  capabilities: [...new Set((server.tools ?? []).map((tool) => tool.kind))].map(
    (kind) => kind[0].toUpperCase() + kind.slice(1),
  ),
  serverUrl: server.endpoint || null,
  transport: 'streamable_http',
  authModes: ['api_key'],
  apiKey: {
    headerName: 'Authorization',
    scheme: 'Bearer',
    prefix: 'ctr_',
    keyName: 'key for this server',
    settingsLabel: 'this server’s Keys section in Capitol',
  },
  capitol: {
    ownership: 'organization',
    method: 'gateway_key',
    note: 'Keys are minted by org admins and act as the org’s service identity. Give each client its own key so you can revoke it alone.',
  },
  examplePrompts: ['List the workflows I can run.'],
});

const TOGETHER_DOWNLOADS = `${TOGETHER_ORIGIN}/downloads/together-fund`;

// The Together Fund connector (capitol-tools-runner) signs members in through
// TogetherOS. Its OAuth server accepts Claude's and ChatGPT's callbacks and
// loopback redirects only, so Cursor, which also registers cursor.com's
// callback, is refused at registration.
const TOGETHER_FUND: AiAppsServer = {
  featured: true,
  configName: 'together-fund',
  profile: {
    id: 'together-fund',
    name: 'Together Fund',
    description:
      "Send pitches, decks and inbox scans into the Together Fund pipeline as yourself, follow each run to its brief and rating, and search the fund's companies and your own Sentra.",
    capabilities: [
      'Pitches',
      'Decks',
      'Inbox scan',
      'Company search',
      'Sentra',
    ],
    serverUrl: `${TOGETHER_ORIGIN}/together/mcp`,
    transport: 'streamable_http',
    authModes: ['oauth'],
    oauth: {
      discoveryUrl: `${TOGETHER_ORIGIN}/.well-known/oauth-protected-resource/together/mcp`,
      dynamicClientRegistration: true,
    },
    capitol: {
      ownership: 'personal',
      method: 'none',
      note: 'Each member signs in with their own Together Fund Google account, so every pitch, deck and inbox scan is filed as them.',
    },
    examplePrompts: [
      'Run whoami on the Together Fund connector.',
      'What came in this week?',
      'Submit this pitch to Together Fund.',
    ],
    troubleshooting: [
      {
        id: 'not-member',
        title: '"Not a Together Fund member account"',
        body: "You're signed in to TogetherOS with an account that isn't on the fund's member list. Sign out of TogetherOS, then connect again with your Together Fund Google account.",
      },
      {
        id: 'wrong-person',
        title: "whoami shows someone else's email",
        body: 'Stop and tell Tom. Then disconnect Together Fund in the app and connect again, signing in as yourself.',
      },
      {
        id: 'disconnect',
        title: 'Disconnect, or connect again later',
        body: 'Claude: Customize > Connectors > Together Fund > Disconnect. ChatGPT: open the plugin and disconnect it. Claude Code: claude mcp remove together-fund-oauth. A connection lasts up to 90 days; after that the app asks you to connect again.',
      },
    ],
  },
  signIn: {
    apps: ['claude', 'claude-code', 'chatgpt'],
    hint: 'A TogetherOS page opens. Sign in with your Together Fund Google account if it asks, check the page names the app you are connecting and shows your own email, then click **Allow**.',
    blocked: {
      cursor:
        "Cursor's sign-in isn't accepted by the Together Fund connector yet, so Cursor can only use a member key.",
    },
    // Matches the together-fund-intake skill's own setup instructions.
    configName: 'together-fund-oauth',
  },
  // The key-based address and file names match the skill's setup guide.
  keyAccess: {
    serverUrl: `${TOGETHER_ORIGIN}/mcp`,
    apiKey: {
      headerName: 'Authorization',
      scheme: 'Bearer',
      prefix: 'tfm_',
      keyName: 'member key',
    },
    issuedBy: 'Tom',
    keyFile: '~/.together-fund/mcp-key',
    headerFile: '~/.together-fund/mcp-headers.txt',
    envVar: 'TOGETHER_MCP_KEY',
  },
  verify: {
    prompt: 'Run whoami on the Together Fund connector.',
    expect:
      "It answers with your own email. If it shows anyone else's, stop and tell Tom.",
  },
  plugin: {
    id: 'together-fund-claude-plugin',
    label: 'Together Fund plugin',
    fileName: 'together-fund-claude-plugin.zip',
    url: `${TOGETHER_DOWNLOADS}/together-fund-claude-plugin.zip`,
    description:
      'The Together Fund connector and both skills in one install. No keys inside.',
  },
  skills: [
    {
      id: 'together-fund-intake',
      label: 'together-fund-intake skill',
      fileName: 'together-fund-intake.zip',
      url: `${TOGETHER_DOWNLOADS}/together-fund-intake.zip`,
      description:
        'Submits pitches, decks and inbox scans, follows each run and searches companies, through the connector.',
    },
    {
      id: 'deal-onepager',
      label: 'deal-onepager skill',
      fileName: 'deal-onepager.zip',
      url: `${TOGETHER_DOWNLOADS}/deal-onepager.zip`,
      description:
        'Drafts a one-page deal sheet in the chat. Needs no connector.',
    },
  ],
};

const FEATURED_BY_ORG: Record<string, AiAppsServer[]> = {
  [TOGETHER_ORG_ID]: [TOGETHER_FUND],
};

export const featuredServers = (orgId?: string): AiAppsServer[] =>
  orgId ? (FEATURED_BY_ORG[orgId] ?? []) : [];

export const gatewayServer = (server: McpServer): AiAppsServer => {
  const profile = profileFromGatewayServer(server);
  const keyFile = keyFilePath(profile);
  return {
    featured: false,
    configName: configNameFor(profile),
    profile,
    keyAccess: {
      serverUrl: server.endpoint,
      apiKey: profile.apiKey!,
      issuedBy: 'an org admin',
      keyFile,
      headerFile: keyFile.replace(/\.key$/, '-headers.txt'),
      envVar: keyEnvVar(profile),
    },
    verify: {
      prompt: profile.examplePrompts[0] ?? `What can ${profile.name} do?`,
      expect: `It answers from ${profile.name} instead of guessing.`,
    },
    skills: [],
    manageKeysPath: `${ROUTES.SETTINGS.MCP_SERVERS}/${server.server_id}`,
  };
};

export const connectableGatewayServers = (servers: McpServer[]) =>
  servers
    .filter((server) => server.status === 'active' && Boolean(server.endpoint))
    .map(gatewayServer);
